import { apiClient } from '../client';

/**
 * The structured shape stored under organizations.ai_description.
 * The backend serializes this into a labeled multi-line string at write time
 * and parses it back at read time. See
 * backend/src/services/OrganizationAiContextSerializer.js for the full
 * round-trip contract.
 */
export interface OrganizationAiContextStructured {
  business_name: string;
  website: string;
  instagram_handle: string;
  services: string;
  support_email: string;
  support_phone: string;
  notes: string;
}

export interface OrganizationAiContextResponse {
  hasContent: boolean;
  raw: string;
  parsed: OrganizationAiContextStructured;
}

/**
 * Backend errors come back through the apiClient swallow-error wrapper
 * (see CLAUDE.md "apiClient swallows errors"). Each method returns either
 * the unwrapped success payload OR an `{ error, errors? }` object with
 * `success: false` so callers can branch without try/catch.
 */
export type OrganizationApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; errors?: Record<string, string> };

const empty: OrganizationAiContextStructured = {
  business_name: '',
  website: '',
  instagram_handle: '',
  services: '',
  support_email: '',
  support_phone: '',
  notes: '',
};

/**
 * Read-side helper that always returns a usable response — failures degrade
 * to "no content" so the UI can surface the empty-state banner instead of
 * blocking the whole inspector with an error.
 */
function safeAiContextResponse(payload: any): OrganizationAiContextResponse {
  const parsed = (payload?.parsed && typeof payload.parsed === 'object')
    ? { ...empty, ...payload.parsed }
    : { ...empty };
  return {
    hasContent: Boolean(payload?.hasContent),
    raw: typeof payload?.raw === 'string' ? payload.raw : '',
    parsed,
  };
}

export const organizationApi = {
  /**
   * Fetch the calling user's organization AI context. Always resolves —
   * server errors surface as `ok: false`, the inspector treats `ok: false`
   * the same as `hasContent: false` so a transient backend hiccup does
   * not block the UI.
   */
  async getAiContext(): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.get('/organizations/me/ai-context');
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to load organization context' };
    }
    // apiClient unwraps `{ success, ...payload }` into `{ data: ...payload }`
    // when the response has a `success` boolean. Try both shapes.
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(payload) };
  },

  /**
   * Persist a structured AI context payload. Returns the canonicalized
   * version (after server-side serialize → parse round-trip).
   *
   * On validation failure the backend returns 400 with `errors` keyed by
   * field name. We surface that as `{ ok: false, error, errors }` so the
   * form can render inline errors without a try/catch.
   */
  async updateAiContext(
    payload: Partial<OrganizationAiContextStructured>,
  ): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.put('/organizations/me/ai-context', payload);
    if (res?.error || res?.success === false) {
      // apiClient lifts top-level `errors` into `res.errors` when present.
      const errors =
        (res?.errors && typeof res.errors === 'object' ? res.errors : undefined) ??
        (res?.data?.errors && typeof res.data.errors === 'object' ? res.data.errors : undefined);
      return {
        ok: false,
        error: res?.error || 'Failed to update organization context',
        errors,
      };
    }
    const body = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(body) };
  },

  async clearAiContext(): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.delete('/organizations/me/ai-context');
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to clear organization context' };
    }
    return { ok: true, data: { hasContent: false, raw: '', parsed: {} as OrganizationAiContextStructured } };
  },
};

export const EMPTY_ORG_AI_CONTEXT: OrganizationAiContextStructured = empty;

// ── Brand Websites ──────────────────────────────────────────────────

export interface BrandWebsite {
  website_url: string;
  site_name: string;
  colors: string[];
  description: string;
  logo_url: string;
  design_style: string;
  brand_tone: string;
  target_audience: string;
  suggested_tagline: string;
  created_at?: string;
  updated_at?: string;
}

export const brandWebsitesApi = {
  async list(): Promise<{ ok: boolean; websites: BrandWebsite[] }> {
    const res: any = await apiClient.get('/organizations/me/brand-websites');
    if (res?.error || res?.success === false) {
      return { ok: false, websites: [] };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return {
      ok: true,
      websites: Array.isArray(payload?.websites) ? payload.websites : [],
    };
  },

  async add(websiteUrl: string): Promise<{ ok: boolean; website?: BrandWebsite; error?: string }> {
    const res: any = await apiClient.post('/organizations/me/brand-websites', { website_url: websiteUrl });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to analyze website' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, website: payload?.website };
  },

  async update(websiteUrl: string, updates: Partial<BrandWebsite>): Promise<{ ok: boolean; website?: BrandWebsite; error?: string }> {
    const encoded = btoa(websiteUrl);
    const res: any = await apiClient.put(`/organizations/me/brand-websites/${encoded}`, updates);
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to update website' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, website: payload };
  },

  async remove(websiteUrl: string): Promise<{ ok: boolean; error?: string }> {
    const encoded = btoa(websiteUrl);
    const res: any = await apiClient.delete(`/organizations/me/brand-websites/${encoded}`);
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to remove website' };
    }
    return { ok: true };
  },
};

// ── Daily Leads Email ──────────────────────────────────────────────

export const dailyLeadsEmailApi = {
  async getSettings(): Promise<{ ok: boolean; time: string | null; additionalEmails: string[] }> {
    const res: any = await apiClient.get('/organizations/me/daily-leads-email');
    if (res?.error || res?.success === false) {
      return { ok: false, time: null, additionalEmails: [] };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return {
      ok: true,
      time: payload?.time ?? null,
      additionalEmails: Array.isArray(payload?.additionalEmails) ? payload.additionalEmails : [],
    };
  },

  async saveSettings(time: string | null, additionalEmails: string[]): Promise<{ ok: boolean; error?: string }> {
    const res: any = await apiClient.put('/organizations/me/daily-leads-email', { time, additionalEmails });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to update schedule' };
    }
    return { ok: true };
  },
};
